import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Target,
  Code2,
  BrainCircuit,
  Rocket,
  GraduationCap,
  FileText,
  Users,
  Award,
  Brain,
  Layers,
  Cloud,
  GitBranch,
  ShieldCheck,
  MessageSquare,
  BarChart3,
  Building2,
  Star,
  Share2,
  Gift,
  Wallet,
  ClipboardCheck,
  PenTool,
  Activity,
  RefreshCw,
  UserPlus,
  Link2,
  Send,
  NotebookPen,
  LayoutDashboard,
  LineChart,
  Compass,
  Timer,
  Server,
  ChevronLeft,
  ChevronRight,
  Quote
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Footer from "./Footer";
import SaaSHeader from "./SaaSHeader";

/* ─────────────────────────────────────────────────────────────────────────────
   Shared animation helpers
───────────────────────────────────────────────────────────────────────────── */

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

const FloatingCard = ({ children, delay = 0, className = "" }) => (
  <motion.div
    animate={{ y: [0, -9, 0] }}
    transition={{ duration: 4 + delay * 0.6, repeat: Infinity, ease: "easeInOut", delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const SectionBadge = ({ icon: Icon, children, tone = "purple" }) => {
  const tones = {
    purple: "bg-purple-50 border-purple-100 text-purple-700",
    indigo: "bg-indigo-50 border-indigo-100 text-indigo-700",
    blue: "bg-blue-50 border-blue-100 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    amber: "bg-amber-50 border-amber-100 text-amber-700",
    dark: "bg-white/10 border-white/15 text-purple-200"
  };
  return (
    <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-black uppercase tracking-widest ${tones[tone]}`}>
      {Icon && <Icon size={13} aria-hidden="true" />}
      {children}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   HERO — animated product dashboard (right side)
───────────────────────────────────────────────────────────────────────────── */

const HeroDashboard = () => (
  <div className="relative h-[600px]" aria-hidden="true">
    {/* soft glow behind the cluster */}
    <div className="absolute inset-6 bg-gradient-to-br from-purple-200/40 via-indigo-100/40 to-cyan-100/30 rounded-[56px] blur-3xl" />

    {/* AI Mentor */}
    <FloatingCard delay={0} className="absolute top-0 left-0 w-[260px] z-10">
      <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl shadow-purple-100/60 border border-gray-100 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
            <Brain size={16} className="text-white" />
          </div>
          <div>
            <div className="text-xs font-black text-gray-900">AI Mentor</div>
            <div className="text-[10px] text-emerald-500 font-bold flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" /> Online 24/7
            </div>
          </div>
        </div>
        <div className="bg-purple-50 rounded-xl p-3 text-[11px] text-purple-700 font-semibold leading-relaxed">
          "Sprint 3 review passed. Next: harden your API error handling before deployment."
        </div>
      </div>
    </FloatingCard>

    {/* Career Roadmap */}
    <FloatingCard delay={1} className="absolute top-6 right-0 w-[220px] z-10">
      <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl shadow-indigo-100/60 border border-gray-100 p-4">
        <div className="text-xs font-black text-gray-900 mb-3 flex items-center gap-2">
          <Compass size={13} className="text-indigo-600" /> Career Roadmap
        </div>
        {[
          { label: "Core Engineering", pct: 100, color: "bg-emerald-500" },
          { label: "Real Projects", pct: 76, color: "bg-indigo-500" },
          { label: "System Design", pct: 48, color: "bg-purple-500" }
        ].map((r, i) => (
          <div key={i} className="mb-2.5">
            <div className="flex justify-between text-[10px] text-gray-500 font-bold mb-1">
              <span>{r.label}</span>
              <span>{r.pct}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${r.pct}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, delay: 0.3 + i * 0.15, ease: "easeOut" }}
                className={`h-full ${r.color} rounded-full`}
              />
            </div>
          </div>
        ))}
      </div>
    </FloatingCard>

    {/* GitHub Activity */}
    <FloatingCard delay={0.5} className="absolute top-[200px] left-2 w-[236px] z-10">
      <div className="bg-gray-900 rounded-2xl shadow-xl shadow-gray-400/30 p-4">
        <div className="text-xs font-black text-white mb-3 flex items-center gap-2">
          <GitBranch size={13} className="text-purple-400" /> GitHub Activity
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {Array(28).fill(0).map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.6 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 + i * 0.02 }}
              className={`w-full aspect-square rounded-[3px] ${
                [0, 3, 5, 8, 10, 13, 15, 18, 20, 22, 25, 27].includes(i)
                  ? "bg-purple-500"
                  : [1, 6, 11, 16, 21, 26].includes(i)
                  ? "bg-purple-700/50"
                  : "bg-gray-800"
              }`}
            />
          ))}
        </div>
        <div className="text-[10px] text-purple-400 font-bold">14 commits · 3 PRs merged this week</div>
      </div>
    </FloatingCard>

    {/* Mock Interview Score */}
    <FloatingCard delay={1.5} className="absolute top-[170px] right-3 w-[210px] z-10">
      <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl shadow-emerald-100/60 border border-gray-100 p-4">
        <div className="text-xs font-black text-gray-900 mb-2 flex items-center gap-2">
          <MessageSquare size={13} className="text-emerald-600" /> Mock Interview
        </div>
        <div className="flex items-center gap-3">
          <svg viewBox="0 0 44 44" className="w-14 h-14 -rotate-90">
            <circle cx="22" cy="22" r="18" fill="none" stroke="#F1F5F9" strokeWidth="5" />
            <motion.circle
              cx="22" cy="22" r="18" fill="none"
              stroke="url(#scoreGrad)" strokeWidth="5" strokeLinecap="round"
              strokeDasharray="113"
              initial={{ strokeDashoffset: 113 }}
              whileInView={{ strokeDashoffset: 113 * (1 - 0.91) }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, delay: 0.4, ease: "easeOut" }}
            />
            <defs>
              <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#06B6D4" />
              </linearGradient>
            </defs>
          </svg>
          <div>
            <div className="text-2xl font-black text-gray-900 leading-none">91<span className="text-sm text-gray-400">/100</span></div>
            <div className="text-[10px] text-emerald-600 font-bold mt-1">↑ 14 since last round</div>
          </div>
        </div>
      </div>
    </FloatingCard>

    {/* Deployment progress */}
    <FloatingCard delay={0.8} className="absolute bottom-[104px] left-8 w-[250px] z-10">
      <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl shadow-blue-100/60 border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-black text-gray-900 flex items-center gap-2">
            <Server size={13} className="text-blue-600" /> Deployment
          </div>
          <span className="text-[9px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Cloud</span>
        </div>
        <div className="font-mono text-[10px] text-gray-500 mb-2">deploy: skillgen-app → production</div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: "0%" }}
            whileInView={{ width: "88%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, delay: 0.5, ease: "easeOut" }}
            className="h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"
          />
        </div>
        <div className="flex justify-between text-[9px] font-bold text-gray-400 mt-1.5">
          <span>build ✓ &nbsp; test ✓ &nbsp; deploy…</span>
          <span className="text-blue-600">88%</span>
        </div>
      </div>
    </FloatingCard>

    {/* Production ready badge */}
    <FloatingCard delay={1.2} className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[230px] z-20">
      <div className="bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 rounded-2xl shadow-2xl shadow-purple-300/60 p-4 text-white">
        <div className="text-xs font-black mb-1 flex items-center gap-2">
          <ShieldCheck size={14} /> AI Code Review
        </div>
        <div className="text-[10px] opacity-85 font-medium">Checkout service — logic, edge cases & security verified</div>
        <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-black text-emerald-300">
          <CheckCircle2 size={12} /> Production-Ready
        </div>
      </div>
    </FloatingCard>

    {/* Mini analytics chart */}
    <FloatingCard delay={2} className="absolute bottom-[96px] right-0 w-[190px] z-10">
      <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl shadow-purple-100/50 border border-gray-100 p-4">
        <div className="text-xs font-black text-gray-900 mb-2 flex items-center gap-2">
          <LineChart size={13} className="text-purple-600" /> Skill Growth
        </div>
        <svg viewBox="0 0 140 48" className="w-full h-12">
          <motion.path
            d="M2 42 C 20 40, 28 30, 42 30 S 66 34, 78 24 S 104 10, 118 12 S 132 6, 138 4"
            fill="none" stroke="url(#chartGrad)" strokeWidth="2.5" strokeLinecap="round"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, delay: 0.4, ease: "easeOut" }}
          />
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="60%" stopColor="#6366F1" />
              <stop offset="100%" stopColor="#06B6D4" />
            </linearGradient>
          </defs>
        </svg>
        <div className="text-[10px] font-bold text-emerald-600">+38% in 45 days</div>
      </div>
    </FloatingCard>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   Testimonials data
───────────────────────────────────────────────────────────────────────────── */

const TESTIMONIALS = [
  {
    name: "Sai Kiran",
    role: "Graduate Engineer · Placed in a FinTech startup",
    initials: "SK",
    grad: "from-purple-500 to-indigo-500",
    quote:
      "The 45-day bootcamp changed how I think about code. I walked into my interview with a deployed product, real GitHub history and confidence — the panel spent most of the time reviewing my project."
  },
  {
    name: "Priya Sharma",
    role: "Final-Year Student · Computer Science",
    initials: "PS",
    grad: "from-indigo-500 to-blue-500",
    quote:
      "The AI mock interviews felt more rigorous than my actual campus rounds. Getting scored feedback on every answer showed me exactly what to fix — my score went from 62 to 90 in six weeks."
  },
  {
    name: "Dr. Ramesh Kumar",
    role: "Placement Director · Engineering College",
    initials: "RK",
    grad: "from-blue-500 to-cyan-500",
    quote:
      "The college dashboard gives us cohort-level visibility we never had. Recruiters noticed the difference in our students within one placement season — they come with projects, not just certificates."
  },
  {
    name: "Anusha Reddy",
    role: "Working Professional · Upskilling to Full Stack",
    initials: "AR",
    grad: "from-fuchsia-500 to-purple-500",
    quote:
      "I'd done plenty of video courses before. This was different — actual sprints, code reviews and a production deployment. It felt like joining an engineering team, not taking a class."
  }
];

/* ─────────────────────────────────────────────────────────────────────────────
   Page
───────────────────────────────────────────────────────────────────────────── */

const Home = () => {
  const navigate = useNavigate();
  const [tIndex, setTIndex] = useState(0);

  const handleStartLearning = () => window.open("https://www.theskillgenie.com/nextgen-builder/register", "_blank");

  // testimonial auto-advance
  useEffect(() => {
    const id = setInterval(() => setTIndex((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="bg-white text-gray-900 min-h-screen overflow-x-hidden font-sans selection:bg-purple-100 selection:text-purple-900 scroll-smooth">
      <SaaSHeader />

      {/* ══ HERO ══════════════════════════════════════════════════════════ */}
      <section className="relative w-full flex items-center pt-36 pb-24 px-6 md:px-12 overflow-hidden" aria-label="Hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[60%] h-[80%] bg-gradient-to-bl from-purple-100/70 via-indigo-50/50 to-transparent blur-[110px] rounded-full" />
          <div className="absolute bottom-0 left-0 w-[45%] h-[55%] bg-gradient-to-tr from-cyan-50/60 to-transparent blur-[110px] rounded-full" />
          <div className="absolute inset-0 opacity-[0.018]" style={{ backgroundImage: "radial-gradient(#6D4AFF 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        </div>

        <div className="max-w-[1400px] mx-auto relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/80 backdrop-blur border border-purple-200 text-purple-700 text-sm font-bold mb-8 shadow-sm shadow-purple-100"
              >
                <Rocket size={14} className="text-purple-600" aria-hidden="true" />
                AI-Powered Industry Readiness Platform
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.12 }}
                className="text-[2.6rem] md:text-6xl font-black leading-[1.06] tracking-tight mb-6"
              >
                <span className="text-gray-900">Empowering Future Builders.</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600">
                  Engineering Real-World Impact.
                </span>
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xl md:text-2xl font-black tracking-tight mb-6"
                aria-label="Learn. Build. Deploy. Succeed."
              >
                {["Learn.", "Build.", "Deploy.", "Succeed."].map((w, i) => (
                  <motion.span
                    key={w}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + i * 0.14 }}
                    className={i === 3 ? "text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-cyan-500" : "text-gray-400"}
                  >
                    {w}
                  </motion.span>
                ))}
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="text-lg text-gray-500 leading-relaxed font-medium mb-9 max-w-xl"
              >
                The Skill Genie helps students, graduates and professionals gain practical engineering
                skills through AI-powered learning, real-world projects, mock interviews, coding
                practice and deployment-focused bootcamps—bridging the gap between education and industry.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.45 }}
                className="flex flex-col sm:flex-row gap-4 mb-9"
              >
                <button
                  onClick={handleStartLearning}
                  className="group flex items-center justify-center gap-2 px-9 py-4 text-white rounded-2xl font-bold text-lg bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:shadow-2xl hover:shadow-purple-300/60 hover:-translate-y-0.5 transition-all"
                  aria-label="Start learning — choose your account type"
                >
                  Start Learning
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </button>
                <a
                  href="https://www.theskillgenie.com/book-demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-9 py-4 bg-white text-gray-800 border-2 border-gray-200 rounded-2xl font-bold text-lg hover:border-purple-400 hover:text-purple-700 hover:shadow-lg transition-all"
                >
                  Book a Demo
                </a>
              </motion.div>

              <motion.ul
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex flex-wrap gap-x-5 gap-y-2.5 list-none p-0 m-0"
                aria-label="Platform highlights"
              >
                {["AI Mentor", "Real Projects", "Mock Interviews", "GitHub Based Learning", "Career Support"].map((t) => (
                  <li key={t} className="flex items-center gap-1.5 text-sm text-gray-500 font-bold">
                    <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0" aria-hidden="true" />
                    {t}
                  </li>
                ))}
              </motion.ul>
            </div>

            {/* RIGHT — animated dashboard */}
            <motion.div
              initial={{ opacity: 0, x: 32 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              <HeroDashboard />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ MISSION ═══════════════════════════════════════════════════════ */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-gray-50/70" aria-label="Our mission">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <SectionBadge icon={Target}>Our Mission</SectionBadge>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mt-6 mb-6">
              From Learning to{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">
                Engineering Excellence
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-lg md:text-xl text-gray-500 font-medium leading-relaxed max-w-3xl mx-auto mb-12">
              The Skill Genie prepares learners for real software engineering careers by combining
              structured learning, AI guidance, practical coding, real-world projects and deployment experience.
            </p>
          </Reveal>

          <div className="flex flex-wrap justify-center items-center gap-3 md:gap-0">
            {["Learn", "Practice", "Build", "Deploy", "Grow"].map((step, i, arr) => (
              <Reveal key={step} delay={0.25 + i * 0.1} className="flex items-center">
                <span className="px-6 md:px-8 py-3.5 rounded-2xl bg-white border border-gray-100 shadow-md shadow-purple-100/40 text-base md:text-lg font-black text-gray-900 hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-200/50 hover:border-purple-200 transition-all cursor-default">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">{`0${i + 1}`}</span>
                  &nbsp;&nbsp;{step}
                </span>
                {i < arr.length - 1 && (
                  <ArrowRight size={18} className="hidden md:block mx-3 text-gray-300 flex-shrink-0" aria-hidden="true" />
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ REAL WORLD EXPERIENCE — 45 DAYS ═══════════════════════════════ */}
      <section className="py-20 px-6 bg-[#0A0C14] text-white overflow-hidden relative" aria-label="45 days real world experience">
        <div className="absolute top-0 left-1/4 w-[500px] h-[300px] bg-purple-700/20 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[260px] bg-blue-700/15 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-[1400px] mx-auto relative z-10">
          <Reveal className="text-center mb-14">
            <SectionBadge icon={Timer} tone="dark">Real World Experience</SectionBadge>
            <h2 className="text-3xl md:text-6xl font-black tracking-tight leading-[1.08] mt-6 mb-5 text-white">
              45 Days.{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-indigo-400 to-cyan-400">One Real Product.</span>
              <br />Lifetime Experience.
            </h2>
            <p className="text-lg text-gray-400 font-medium max-w-2xl mx-auto">
              Work on challenging production-grade SaaS applications from local development to cloud deployment.
            </p>
          </Reveal>

          {/* Timeline */}
          <div className="relative mb-16">
            <div className="hidden md:block absolute top-[26px] left-[6%] right-[6%] h-px bg-gradient-to-r from-purple-500/0 via-purple-500/50 to-cyan-500/0" aria-hidden="true" />
            <ol className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-6 list-none p-0 m-0">
              {[
                { label: "Plan", icon: ClipboardCheck },
                { label: "Design", icon: PenTool },
                { label: "Develop", icon: Code2 },
                { label: "Test", icon: ShieldCheck },
                { label: "Deploy", icon: Cloud },
                { label: "Monitor", icon: Activity },
                { label: "Improve", icon: RefreshCw }
              ].map((s, i) => (
                <Reveal key={s.label} delay={i * 0.08}>
                  <li className="flex flex-col items-center text-center group">
                    <div className="w-[52px] h-[52px] rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center mb-3 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-indigo-600 group-hover:border-transparent group-hover:scale-110 transition-all duration-300 relative z-10">
                      <s.icon size={22} className="text-purple-300 group-hover:text-white transition-colors" aria-hidden="true" />
                    </div>
                    <span className="text-sm font-black text-gray-300">{s.label}</span>
                    <span className="text-[10px] font-bold text-gray-400 mt-0.5">{`Phase ${i + 1}`}</span>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { value: "45 Days", desc: "Structured sprint journey" },
              { value: "100% Hands-On", desc: "No passive video watching" },
              { value: "Real GitHub Commits", desc: "Genuine contribution history" },
              { value: "Cloud Deployment", desc: "Ship to live infrastructure" },
              { value: "Production Reviews", desc: "Code reviewed like a real PR" },
              { value: "AI Feedback", desc: "On every single submission" }
            ].map((m, i) => (
              <Reveal key={m.value} delay={i * 0.06}>
                <div className="h-full p-5 rounded-2xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.07] hover:border-purple-500/30 transition-all text-center">
                  <div className="text-base md:text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-cyan-300 mb-1">{m.value}</div>
                  <div className="text-xs text-gray-500 font-semibold">{m.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ LEARNING ECOSYSTEM ════════════════════════════════════════════ */}
      <section id="features" className="py-20 px-6 bg-gray-50/80 scroll-mt-24" aria-label="Learning ecosystem">
        <div className="max-w-[1400px] mx-auto">
          <Reveal className="text-center mb-14">
            <SectionBadge icon={Layers} tone="blue">Learning Ecosystem</SectionBadge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mt-6 mb-4">
              One Platform.{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                Every Tool You Need.
              </span>
            </h2>
            <p className="text-lg text-gray-500 font-medium max-w-2xl mx-auto">
              A complete ecosystem covering the entire journey — from first concept to career offer.
            </p>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {[
              { icon: Brain, label: "AI Learning", color: "text-purple-600 bg-purple-50" },
              { icon: Code2, label: "Coding Practice", color: "text-indigo-600 bg-indigo-50" },
              { icon: Layers, label: "Projects", color: "text-blue-600 bg-blue-50" },
              { icon: MessageSquare, label: "Mock Interviews", color: "text-emerald-600 bg-emerald-50" },
              { icon: NotebookPen, label: "Notes", color: "text-amber-600 bg-amber-50" },
              { icon: FileText, label: "Resume Builder", color: "text-orange-600 bg-orange-50" },
              { icon: ClipboardCheck, label: "Assessments", color: "text-pink-600 bg-pink-50" },
              { icon: Compass, label: "Career Guidance", color: "text-cyan-600 bg-cyan-50" },
              { icon: Gift, label: "Referral Rewards", color: "text-fuchsia-600 bg-fuchsia-50" },
              { icon: LayoutDashboard, label: "Student Dashboard", color: "text-violet-600 bg-violet-50" },
              { icon: Building2, label: "College Dashboard", color: "text-blue-700 bg-blue-50" },
              { icon: BarChart3, label: "Analytics", color: "text-teal-600 bg-teal-50" }
            ].map((f, i) => (
              <Reveal key={f.label} delay={(i % 4) * 0.06}>
                <div className="group h-full p-6 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-purple-100/60 hover:-translate-y-1 hover:border-purple-100 transition-all duration-300 flex flex-col items-start">
                  <div className={`w-12 h-12 rounded-2xl ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <f.icon size={22} aria-hidden="true" />
                  </div>
                  <h3 className="text-[15px] font-black text-gray-900">{f.label}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ BOOTCAMP ══════════════════════════════════════════════════════ */}
      <section className="py-20 px-6 bg-gray-50/80 overflow-hidden" aria-label="Bootcamp">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

            <Reveal>
              <SectionBadge icon={Rocket} tone="indigo">TSG Nexus</SectionBadge>
              <h2 className="text-3xl md:text-5xl font-black text-gray-900 mt-6 mb-5 tracking-tight leading-tight">
                Build Like an Engineer.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">Not Just a Student.</span>
              </h2>
              <p className="text-lg text-gray-500 font-medium leading-relaxed mb-8">
                The bootcamp simulates a real engineering team. Daily tasks, sprint reviews, GitHub
                workflow and AI code reviews — ending with a deployed product you own end to end.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-9">
                {[
                  { icon: Timer, label: "45-Day Journey" },
                  { icon: Users, label: "Real Team Collaboration" },
                  { icon: GitBranch, label: "GitHub Workflow" },
                  { icon: BrainCircuit, label: "AI Code Reviews" },
                  { icon: ClipboardCheck, label: "Daily Tasks" },
                  { icon: Target, label: "Sprint Reviews" },
                  { icon: Cloud, label: "Deployment" },
                  { icon: Users, label: "Live Mentorship" }
                ].map((f, i) => (
                  <Reveal key={f.label} delay={i * 0.05}>
                    <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-2xl border border-gray-100 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all">
                      <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
                        <f.icon size={17} aria-hidden="true" />
                      </div>
                      <span className="text-sm font-black text-gray-800">{f.label}</span>
                    </div>
                  </Reveal>
                ))}
              </div>

              <a
                href="https://www.theskillgenie.com/nextgen-builder/register"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-8 py-4 text-white rounded-2xl font-bold text-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:shadow-xl hover:shadow-indigo-300/50 hover:-translate-y-0.5 transition-all"
              >
                Explore TSG Nexus
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </a>
            </Reveal>

            {/* Right — sprint board illustration */}
            <Reveal delay={0.15}>
              <div className="relative p-1 bg-gradient-to-br from-purple-500 via-indigo-500 to-cyan-500 rounded-[36px] shadow-2xl shadow-indigo-300/30">
                <div className="bg-white rounded-[32px] p-7 md:p-9">
                  <div className="flex items-center justify-between mb-7">
                    <div className="text-xs font-black text-gray-400 uppercase tracking-widest">Sprint 3 · Week 5 of 7</div>
                    <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full uppercase">On Track</span>
                  </div>
                  <div className="space-y-5">
                    {[
                      { step: "01", label: "Daily Task", desc: "Implement payment webhook + tests", status: "done" },
                      { step: "02", label: "Pull Request", desc: "feature/webhooks → 2 review comments", status: "done" },
                      { step: "03", label: "AI Code Review", desc: "Edge cases: retry logic flagged & fixed", status: "active" },
                      { step: "04", label: "Sprint Review", desc: "Demo to mentors — Friday 4 PM", status: "next" },
                      { step: "05", label: "Deploy", desc: "Staging → Production rollout", status: "next" }
                    ].map((s) => (
                      <div key={s.step} className="flex items-start gap-4">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0 ${
                            s.status === "done"
                              ? "bg-emerald-500 text-white"
                              : s.status === "active"
                              ? "bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-200"
                              : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          {s.status === "done" ? "✓" : s.step}
                        </div>
                        <div className="min-w-0">
                          <div className={`font-black text-sm ${s.status === "next" ? "text-gray-400" : "text-gray-900"}`}>{s.label}</div>
                          <div className="text-xs text-gray-400 font-medium truncate">{s.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-7 flex flex-wrap gap-2">
                    {["GitHub", "Agile", "CI/CD", "AI Review", "Cloud"].map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-bold">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══ TSG NEXUS ═════════════════════════════════════════════════════ */}
      {/* Replaces the old basic "Referral Program" block. Same slot in the page
          (after Bootcamp, before Testimonials) so the student proposition still
          leads; repositioned from a referral scheme into a strategic partner
          ecosystem. Reuses Reveal / SectionBadge / lucide icons already in this
          file — no new imports, no new routes. "Join" points at the existing
          NextGen Builder registration where partners get their referral code. */}
      <section id="nexus" className="py-20 px-6 bg-gradient-to-b from-white to-gray-50/70 overflow-hidden scroll-mt-24" aria-label="TSG Nexus partner program">
        <div className="max-w-[1400px] mx-auto">

          {/* Header */}
          <Reveal className="text-center mb-16">
            <SectionBadge icon={Share2} tone="indigo">Introducing TSG Nexus</SectionBadge>
            <h2 className="text-3xl md:text-6xl font-black tracking-tight leading-tight mt-6 mb-4">
              <span className="text-gray-900">More Than a Referral.</span>{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">A Career Impact Network.</span>
            </h2>
            <p className="text-lg text-gray-500 font-medium max-w-3xl mx-auto">
              TSG Nexus connects creators, educators, mentors, developers and student communities with The Skill Genie — helping students become genuinely industry-ready, while you build real impact and earn on every successful referral.
            </p>
          </Reveal>

          {/* Value chain: YOU -> TSG -> STUDENT -> YOU */}
          <div className="relative mb-20">
            <div className="hidden lg:block absolute top-[34px] left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-purple-200 via-indigo-200 to-emerald-200" aria-hidden="true" />
            <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 list-none p-0 m-0">
              {[
                { icon: Share2, label: "You Connect", desc: "Introduce students from your audience or community", tone: "text-purple-600" },
                { icon: Rocket, label: "TSG Delivers", desc: "A real career-readiness ecosystem, not another course", tone: "text-indigo-600" },
                { icon: GraduationCap, label: "Students Grow", desc: "Practical, industry-grade skills before graduation", tone: "text-blue-600" },
                { icon: Award, label: "You Benefit", desc: "Create genuine impact — and earn on every enrollment", tone: "text-emerald-600" }
              ].map((s, i) => (
                <Reveal key={s.label} delay={i * 0.09}>
                  <li className="flex flex-col items-center text-center relative">
                    <div className="w-[68px] h-[68px] rounded-3xl bg-white border border-gray-100 shadow-lg shadow-purple-100/50 flex items-center justify-center mb-4 relative z-10 hover:scale-110 hover:shadow-purple-200/60 transition-all">
                      <s.icon size={26} className={s.tone} aria-hidden="true" />
                      <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 text-white text-[10px] font-black flex items-center justify-center shadow-md">
                        {i + 1}
                      </span>
                    </div>
                    <h3 className="text-base font-black text-gray-900 mb-1">{s.label}</h3>
                    <p className="text-xs text-gray-400 font-semibold max-w-[190px]">{s.desc}</p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>

          {/* Two value columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-16">

            <Reveal>
              <div className="h-full rounded-3xl border border-gray-100 bg-white p-8 shadow-lg shadow-purple-100/30">
                <div className="flex items-center gap-2 mb-6">
                  <GraduationCap size={18} className="text-purple-600" aria-hidden="true" />
                  <h3 className="text-lg font-black text-gray-900">Give students more than a course</h3>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 list-none p-0 m-0">
                  {[
                    { icon: Code2, label: "Real Industry Projects" },
                    { icon: GitBranch, label: "Professional Git Workflows" },
                    { icon: FileText, label: "A Real GitHub Portfolio" },
                    { icon: BrainCircuit, label: "AI-Powered Learning" },
                    { icon: Users, label: "Industry Mentorship" },
                    { icon: MessageSquare, label: "AI Mock Interviews" },
                    { icon: Compass, label: "Career Guidance" },
                    { icon: ClipboardCheck, label: "Interview Readiness" }
                  ].map((b) => (
                    <li key={b.label} className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
                        <b.icon size={16} className="text-purple-600" aria-hidden="true" />
                      </span>
                      <span className="text-sm font-bold text-gray-700">{b.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="h-full rounded-3xl border border-gray-100 bg-white p-8 shadow-lg shadow-indigo-100/30">
                <div className="flex items-center gap-2 mb-6">
                  <Star size={18} className="text-indigo-600" aria-hidden="true" />
                  <h3 className="text-lg font-black text-gray-900">What's in it for you</h3>
                </div>
                <ul className="grid grid-cols-1 gap-4 list-none p-0 m-0">
                  {[
                    { icon: Wallet, label: "Earn on Successful Referrals", desc: "A reward for every student who enrolls" },
                    { icon: GraduationCap, label: "Create Real Student Impact", desc: "Help students gain practical career confidence" },
                    { icon: PenTool, label: "Create Valuable Content", desc: "Around coding, AI, careers and interviews" },
                    { icon: ShieldCheck, label: "Grow Your Credibility", desc: "Recommend something genuinely career-focused" },
                    { icon: Link2, label: "Your Own Referral Identity", desc: "A unique code that's entirely yours" },
                    { icon: Users, label: "Dedicated Partner Support", desc: "Promotional assets, updates and campaign help" }
                  ].map((b) => (
                    <li key={b.label} className="flex items-start gap-3">
                      <span className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
                        <b.icon size={16} className="text-indigo-600" aria-hidden="true" />
                      </span>
                      <div>
                        <p className="text-sm font-black text-gray-900 leading-tight">{b.label}</p>
                        <p className="text-xs text-gray-400 font-semibold mt-0.5">{b.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* Reward table */}
          <Reveal>
            <div className="rounded-3xl bg-[#0A0C14] text-white p-8 md:p-10 shadow-2xl shadow-purple-300/20 mb-14">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div>
                  <SectionBadge icon={Wallet} tone="dark">Nexus Rewards</SectionBadge>
                  <h3 className="text-2xl md:text-3xl font-black tracking-tight mt-4">Every student wins. So do you.</h3>
                </div>
                <p className="text-sm text-gray-400 font-semibold max-w-xs md:text-right">
                  Transparent, one-time rewards per successful enrollment — no fine print.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[520px]">
                  <thead>
                    <tr className="text-left">
                      <th className="text-[11px] font-black uppercase tracking-widest text-gray-400 pb-4 pr-4">Plan</th>
                      <th className="text-[11px] font-black uppercase tracking-widest text-gray-400 pb-4 px-4">Student Benefit</th>
                      <th className="text-[11px] font-black uppercase tracking-widest text-gray-400 pb-4 pl-4 text-right">Nexus Partner Reward</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { plan: "Starter", benefit: "₹500", reward: "₹500" },
                      { plan: "Professional", benefit: "₹1,000", reward: "₹1,000", featured: true },
                      { plan: "Enterprise", benefit: "₹2,500", reward: "₹2,500" }
                    ].map((row) => (
                      <tr key={row.plan} className={`border-t border-white/10 ${row.featured ? "bg-white/[0.04]" : ""}`}>
                        <td className="py-5 pr-4">
                          <span className="text-base font-black text-white">{row.plan}</span>
                          {row.featured && (
                            <span className="ml-2 text-[9px] font-black uppercase tracking-widest text-purple-300 bg-purple-500/15 border border-purple-400/20 rounded-full px-2 py-0.5">Popular</span>
                          )}
                        </td>
                        <td className="py-5 px-4">
                          <span className="text-base font-bold text-gray-300">{row.benefit} off</span>
                        </td>
                        <td className="py-5 pl-4 text-right">
                          <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-indigo-300">{row.reward}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 font-semibold mt-6 flex items-center gap-2">
                <CheckCircle2 size={13} className="text-emerald-400" aria-hidden="true" />
                Rewards are credited after successful student payment.
              </p>
            </div>
          </Reveal>

          {/* Closing idea + CTA */}
          <Reveal className="text-center">
            <h3 className="text-2xl md:text-4xl font-black tracking-tight text-gray-900 mb-3">
              Turn your influence into <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">career impact.</span>
            </h3>
            <p className="text-base text-gray-500 font-medium max-w-2xl mx-auto mb-8">
              Your audience doesn't need another promotion — they need something that genuinely moves their career forward. Help students. Build careers. Grow together.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate("/nextgen-builder/register")}
                className="group inline-flex items-center gap-2 px-9 py-4 text-white rounded-2xl font-bold text-lg bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:shadow-2xl hover:shadow-purple-300/60 hover:-translate-y-0.5 transition-all"
              >
                <Share2 size={18} aria-hidden="true" />
                Join TSG Nexus
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </button>
              <a
                href="#nexus"
                className="inline-flex items-center gap-2 px-9 py-4 rounded-2xl font-bold text-lg text-gray-700 bg-white border border-gray-200 hover:border-purple-300 hover:text-purple-700 transition-all"
              >
                See How It Works
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ TESTIMONIALS ══════════════════════════════════════════════════ */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-purple-50/40 overflow-hidden" aria-label="Testimonials">
        <div className="max-w-4xl mx-auto">
          <Reveal className="text-center mb-12">
            <SectionBadge icon={Quote} tone="purple">Success Stories</SectionBadge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mt-6">
              Loved by Learners.{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">Trusted by Colleges.</span>
            </h2>
          </Reveal>

          <div className="relative">
            <div className="relative min-h-[340px] sm:min-h-[300px]">
              <AnimatePresence exitBeforeEnter>
                <motion.figure
                  key={tIndex}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="bg-white/70 backdrop-blur-xl rounded-[32px] border border-purple-100 shadow-xl shadow-purple-100/50 p-8 md:p-10 m-0"
                >
                  <div className="flex gap-1 mb-5" aria-label="5 out of 5 stars">
                    {Array(5).fill(0).map((_, i) => (
                      <Star key={i} size={18} className="text-amber-400 fill-amber-400" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="text-lg md:text-xl text-gray-700 font-semibold leading-relaxed mb-7 m-0">
                    "{TESTIMONIALS[tIndex].quote}"
                  </blockquote>
                  <figcaption className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${TESTIMONIALS[tIndex].grad} flex items-center justify-center text-white text-lg font-black shadow-lg flex-shrink-0`}>
                      {TESTIMONIALS[tIndex].initials}
                    </div>
                    <div>
                      <div className="font-black text-gray-900">{TESTIMONIALS[tIndex].name}</div>
                      <div className="text-sm text-gray-400 font-semibold">{TESTIMONIALS[tIndex].role}</div>
                    </div>
                  </figcaption>
                </motion.figure>
              </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-5 mt-8">
              <button
                onClick={() => setTIndex((tIndex - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                className="w-11 h-11 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-purple-600 hover:border-purple-300 hover:shadow-md transition-all"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex gap-2">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setTIndex(i)}
                    aria-label={`Show testimonial ${i + 1}`}
                    aria-current={i === tIndex}
                    className={`h-2.5 rounded-full transition-all ${i === tIndex ? "w-8 bg-gradient-to-r from-purple-600 to-indigo-600" : "w-2.5 bg-gray-200 hover:bg-gray-300"}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setTIndex((tIndex + 1) % TESTIMONIALS.length)}
                className="w-11 h-11 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-purple-600 hover:border-purple-300 hover:shadow-md transition-all"
                aria-label="Next testimonial"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══ COLLEGE CTA ═══════════════════════════════════════════════════ */}
      <section className="py-20 px-6 bg-white" aria-label="For colleges">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="relative rounded-[40px] bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-indigo-100 p-10 md:p-16 overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-indigo-200/40 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-purple-200/40 rounded-full blur-3xl pointer-events-none" />

              <div className="relative grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-10 items-center">
                <div>
                  <SectionBadge icon={Building2} tone="blue">For Institutions</SectionBadge>
                  <h2 className="text-3xl md:text-5xl font-black text-gray-900 mt-6 mb-5 tracking-tight leading-tight">
                    Bring AI-Powered Industry Readiness{" "}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">to Your Campus</span>
                  </h2>
                  <p className="text-lg text-gray-500 font-medium leading-relaxed mb-8 max-w-xl">
                    Cohort dashboards, placement-aligned tracks, progress analytics and zero curriculum
                    disruption — everything your placement cell needs to graduate hire-ready engineers.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => navigate("/college-intro")}
                      className="group inline-flex items-center justify-center gap-2 px-8 py-4 text-white rounded-2xl font-bold text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow-xl hover:shadow-blue-300/50 hover:-translate-y-0.5 transition-all"
                    >
                      Partner With Us
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => navigate("/book-demo")}
                      className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-800 border-2 border-gray-200 rounded-2xl font-bold text-lg hover:border-blue-400 hover:text-blue-700 transition-all"
                    >
                      Book a Demo
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: LayoutDashboard, label: "Cohort Dashboards" },
                    { icon: BarChart3, label: "Progress Analytics" },
                    { icon: Target, label: "Placement Tracks" },
                    { icon: ShieldCheck, label: "Zero Disruption" }
                  ].map((c, i) => (
                    <Reveal key={c.label} delay={i * 0.08}>
                      <div className="p-5 bg-white/80 backdrop-blur rounded-2xl border border-indigo-100 shadow-sm text-center hover:shadow-lg hover:-translate-y-1 transition-all">
                        <c.icon size={22} className="text-indigo-600 mx-auto mb-2" aria-hidden="true" />
                        <div className="text-sm font-black text-gray-800">{c.label}</div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default Home;
